import React from 'react';
import {
  isEffectivelyOnline,
  registerNetworkStatusCallback,
} from '@bilderit/offline-link';

type Threshold = Parameters<typeof isEffectivelyOnline>[0];

/**
 * Reactive counterpart of `isEffectivelyOnline(threshold)`: re-renders on
 * connectivity transitions instead of taking a one-off snapshot at render.
 * Uses the same registration (and debounce) as the offline link and the
 * connectivity summary, so "offline" means the same thing everywhere; the
 * registration's cleanup is returned from the effect.
 */
export const useIsEffectivelyOnline = (
  threshold: Threshold,
  debounceMs = 2000
): boolean => {
  const [online, setOnline] = React.useState(() =>
    isEffectivelyOnline(threshold)
  );
  React.useEffect(
    // The registration calls back once, synchronously, with the current
    // state, so the initial snapshot above is corrected immediately if it
    // was already stale by the time the effect ran.
    () => registerNetworkStatusCallback(threshold, setOnline, debounceMs),
    [threshold, debounceMs]
  );
  return online;
};
